import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  ArrowForward,
  CheckCircle,
  Sensors,
  Speed,
  WaterDrop,
} from '@mui/icons-material';
import Navbar from './Navbar';
import Footer from './Footer';
import dom4 from './Assets/dom4.webp';
import bulk1 from './Assets/bulk1.webp';
import { Helmet } from 'react-helmet-async';

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
  color: #0f172a;
`;

const Container = styled.main`
  flex: 1;
  padding-top: 76px;
`;

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.34), transparent 34%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 45%, #075985 100%);
  color: white;
`;

const HeroInner = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 76px 0 60px;
  text-align: center;
`;

const Eyebrow = styled.div`
  width: fit-content;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(186, 230, 253, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bae6fd;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  max-width: 720px;
  margin: 20px auto 18px;
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: 0;
`;

const HeroText = styled.p`
  max-width: 640px;
  margin: 0 auto;
  color: #dbeafe;
  font-size: clamp(1rem, 1.7vw, 1.15rem);
  line-height: 1.8;
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 70px 0 40px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.article)`
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: white;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 28px 70px rgba(15, 23, 42, 0.12);
  }
`;

const ImageWrap = styled.div`
  background: #eff6ff;

  img {
    display: block;
    width: 100%;
    height: 260px;
    object-fit: contain;
    padding: 20px 0;
  }
`;

const CardBody = styled.div`
  padding: 28px;
`;

const CardTitle = styled.h2`
  margin: 0 0 10px;
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 800;
`;

const CardText = styled.p`
  margin: 0 0 20px;
  color: #64748b;
  font-size: 0.98rem;
  line-height: 1.7;
`;

const SpecList = styled.ul`
  display: grid;
  gap: 10px;
  padding: 0;
  margin: 0 0 24px;
  list-style: none;
`;

const SpecItem = styled.li`
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 10px;
  align-items: start;
  color: #334155;
  font-size: 0.94rem;
  line-height: 1.6;

  strong {
    color: #0f172a;
  }

  svg {
    margin-top: 3px;
    font-size: 1rem;
    color: #0ea5e9;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid #e2e8f0;
`;

const Price = styled.div`
  span {
    display: block;
    color: #94a3b8;
    font-size: 0.78rem;
    text-transform: uppercase;
    font-weight: 700;
  }

  strong {
    color: #2563eb;
    font-size: 1.25rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: #2563eb;
  color: white;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.24);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
  }
`;

const FinalCta = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 40px auto 80px;
  padding: clamp(34px, 6vw, 58px);
  border-radius: 28px;
  background:
    radial-gradient(circle at 90% 20%, rgba(125, 211, 252, 0.22), transparent 32%),
    linear-gradient(135deg, #071428, #0f3a5f);
  color: white;
  text-align: center;

  h2 {
    max-width: 700px;
    margin: 0 auto;
    font-size: clamp(1.8rem, 3.6vw, 2.5rem);
    line-height: 1.15;
  }

  p {
    max-width: 600px;
    margin: 16px auto 26px;
    color: #dbeafe;
    line-height: 1.8;
  }
`;

const HeroActions = styled.div`
  display: flex;
  justify-content: center;
`;

const meters = [
  {
    image: dom4,
    name: 'DN15 - DN25 Smart Meter',
    description:
      'Compact smart water meter for residential and small commercial applications, built for accurate, low-maintenance metering.',
    size: 'DN15 - DN25',
    features: 'Smart metering, remote monitoring',
    price: 'R250 - R400',
  },
  {
    image: bulk1,
    name: 'DN40 - DN300 Bulk Meter',
    description:
      'High-capacity bulk water meter ideal for large buildings, industrial use, and municipal water distribution networks.',
    size: 'DN40 - DN300',
    features: 'Smart metering, pressure sensors',
    price: 'R400',
  },
];

const Products = () => {
  return (
    <Main>
      <Helmet>
        <title>Smart Water Meters | AquaTools Products</title>
        <meta
          name="description"
          content="Explore AquaTools smart water meters, from compact domestic DN15-DN25 meters to high-capacity DN40-DN300 bulk meters with remote monitoring."
        />
        <meta name="keywords" content="smart water meter, bulk water meter, domestic water meter, AquaTools products" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navbar />

      <Container>
        <Hero>
          <HeroInner>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <Eyebrow>
                <WaterDrop fontSize="small" />
                Our Products
              </Eyebrow>
              <Heading>Smart Water Meters Built for Every Scale</Heading>
              <HeroText>
                From compact domestic meters to high-capacity bulk meters, every AquaTools device is
                built for precision metering, remote monitoring, and seamless integration.
              </HeroText>
            </motion.div>
          </HeroInner>
        </Hero>

        <Section>
          <Grid>
            {meters.map(({ image, name, description, size, features, price }, index) => (
              <ProductCard
                key={name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ImageWrap>
                  <img src={image} alt={name} />
                </ImageWrap>
                <CardBody>
                  <CardTitle>{name}</CardTitle>
                  <CardText>{description}</CardText>
                  <SpecList>
                    <SpecItem>
                      <Sensors />
                      <span><strong>Size:</strong> {size}</span>
                    </SpecItem>
                    <SpecItem>
                      <CheckCircle />
                      <span><strong>Features:</strong> {features}</span>
                    </SpecItem>
                    <SpecItem>
                      <Speed />
                      <span><strong>Precision:</strong> High-accuracy ultrasonic sensing</span>
                    </SpecItem>
                  </SpecList>
                  <PriceRow>
                    <Price>
                      <span>Price range</span>
                      <strong>{price}</strong>
                    </Price>
                    <StyledLink to="/contactus">
                      <Button>
                        Enquire
                        <ArrowForward fontSize="small" />
                      </Button>
                    </StyledLink>
                  </PriceRow>
                </CardBody>
              </ProductCard>
            ))}
          </Grid>
        </Section>

        <FinalCta>
          <h2>Not sure which meter fits your site?</h2>
          <p>
            Tell us about your project and we'll help you match the right meter category and
            deployment plan.
          </p>
          <HeroActions>
            <StyledLink to="/contactus">
              <Button style={{ minHeight: 48, padding: '0 22px' }}>
                Talk to us
                <ArrowForward fontSize="small" />
              </Button>
            </StyledLink>
          </HeroActions>
        </FinalCta>
      </Container>

      <Footer />
    </Main>
  );
};

export default Products;
